import React from 'react'

import {
    Card,
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Chip,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'

import {
    Lead,
    LeadSource,
    LeadSyncStatus,
    LeadSyncStatusDisplay,
} from '../../../../models/Outreach/types'

interface LeadsTableProps {
    leads: Lead[]
    pagination: {
        count: number
        next: string | null
        previous: string | null
        currentPage: number
        pageSize: number
        totalPages: number
    }
    isLoading: boolean
    syncLead: (leadId: number) => void
    onPageChange: (event: unknown, newPage: number) => void
    onRowsPerPageChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    onRowClick?: (lead: Lead) => void
}

const useStyles = makeStyles((theme: Theme) => ({
    tableContainer: {
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 400px)', // Adjust based on your header/stats height
    },
    scrollableTableContainer: {
        overflowX: 'auto',
        overflowY: 'auto',
        flex: 1,
    },
    table: {
        '& .MuiTableCell-root': {
            borderBottom: '1px solid #e2e8f0',
        },
    },
    tableHead: {
        backgroundColor: '#f8fafc',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        '& .MuiTableCell-root': {
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '0.875rem',
            backgroundColor: '#f8fafc', // Ensure background is solid for sticky effect
        },
    },
    tableRow: {
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: '#f8fafc',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
        },
    },
    tableCell: {
        padding: theme.spacing(2),
    },
}))

const getLeadSourceColor = (source: LeadSource): string => {
    switch (source) {
        case LeadSource.EMAIL:
            return '#10b981'
        case LeadSource.PHONE:
            return '#f59e0b'
        case LeadSource.PROPERTY:
            return '#8b5cf6'
        default:
            return '#6b7280'
    }
}

const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'N/A'
    try {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    } catch {
        return 'N/A'
    }
}

export const LeadsTable: React.FC<LeadsTableProps> = ({
    leads,
    pagination,
    isLoading,
    syncLead,
    onPageChange,
    onRowsPerPageChange,
    onRowClick,
}) => {
    const classes = useStyles()

    // Calculate valid page number (0-indexed for TablePagination)
    const validPage = React.useMemo(() => {
        if (!pagination || pagination.count === 0) {
            return 0
        }
        const maxPage = Math.max(
            0,
            Math.ceil(pagination.count / pagination.pageSize) - 1,
        )
        const currentPage = Math.max(
            0,
            Math.min(pagination.currentPage - 1, maxPage),
        )
        return currentPage
    }, [pagination])

    if (isLoading) {
        return (
            <Card className={classes.tableContainer}>
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="400px"
                >
                    <CircularProgress />
                </Box>
            </Card>
        )
    }

    return (
        <Card className={classes.tableContainer}>
            <TableContainer className={classes.scrollableTableContainer}>
                <Table className={classes.table} style={{ width: '100%' }}>
                    <TableHead className={classes.tableHead}>
                        <TableRow>
                            <TableCell className={classes.tableCell}>
                                Name
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Source
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Campaign
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Generated On
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Guest Card Completed
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                                Entrata Sync Status
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {leads.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={6}
                                    className={classes.tableCell}
                                >
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        style={{ color: '#9ca3af' }}
                                    >
                                        No leads found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            leads.map((lead) => (
                                <TableRow
                                    key={lead.id}
                                    className={classes.tableRow}
                                    onClick={() => onRowClick?.(lead)}
                                    style={{
                                        cursor: onRowClick
                                            ? 'pointer'
                                            : 'default',
                                    }}
                                >
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2">
                                            {lead.campaign_prospect?.prospect
                                                ?.name || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Chip
                                            label={
                                                lead.lead_source
                                                    .charAt(0)
                                                    .toUpperCase() +
                                                lead.lead_source.slice(1)
                                            }
                                            size="small"
                                            style={{
                                                backgroundColor: getLeadSourceColor(
                                                    lead.lead_source,
                                                ),
                                                color: 'white',
                                                minWidth: 100,
                                                justifyContent: 'center',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2">
                                            {lead.campaign_prospect?.campaign
                                                ?.name || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Typography variant="body2">
                                            {formatDate(lead.created_date)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        <Chip
                                            label={
                                                lead.guest_card_completed
                                                    ? 'Yes'
                                                    : 'No'
                                            }
                                            size="small"
                                            style={{
                                                backgroundColor: lead.guest_card_completed
                                                    ? '#10b981'
                                                    : '#6b7280',
                                                color: 'white',
                                                minWidth: 60,
                                                justifyContent: 'center',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell className={classes.tableCell}>
                                        {(() => {
                                            const getSyncStatusColor = (
                                                status: LeadSyncStatus,
                                            ) => {
                                                switch (status) {
                                                    case LeadSyncStatus.SYNCED:
                                                        return '#10b981' // green
                                                    case LeadSyncStatus.SYNC_PENDING:
                                                        return '#f59e0b' // amber
                                                    case LeadSyncStatus.SYNC_FAILED:
                                                        return '#ef4444' // red
                                                    case LeadSyncStatus.NOT_SYNCED:
                                                    default:
                                                        return '#6b7280' // gray
                                                }
                                            }

                                            const getSyncStatusLabel = (
                                                status: LeadSyncStatus,
                                            ) => {
                                                switch (status) {
                                                    case LeadSyncStatus.SYNCED:
                                                        return LeadSyncStatusDisplay.SYNCED
                                                    case LeadSyncStatus.SYNC_PENDING:
                                                        return LeadSyncStatusDisplay.SYNC_PENDING
                                                    case LeadSyncStatus.SYNC_FAILED:
                                                        return LeadSyncStatusDisplay.SYNC_FAILED
                                                    case LeadSyncStatus.NOT_SYNCED:
                                                    default:
                                                        return 'Click to sync'
                                                }
                                            }

                                            const syncStatus =
                                                lead.sync_status ||
                                                LeadSyncStatus.NOT_SYNCED
                                            const isNotSynced =
                                                syncStatus ===
                                                LeadSyncStatus.NOT_SYNCED

                                            return (
                                                <Chip
                                                    label={getSyncStatusLabel(
                                                        syncStatus,
                                                    )}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: getSyncStatusColor(
                                                            syncStatus,
                                                        ),
                                                        color: 'white',
                                                        cursor: 'pointer',
                                                        minWidth: 100,
                                                        justifyContent:
                                                            'center',
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        if (isNotSynced) {
                                                            syncLead(lead.id)
                                                        } else {
                                                            console.log(
                                                                'Sync status clicked:',
                                                                syncStatus,
                                                                'Lead ID:',
                                                                lead.id,
                                                            )
                                                        }
                                                    }}
                                                />
                                            )
                                        })()}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination && pagination.count > 0 && (
                <TablePagination
                    component="div"
                    count={pagination.count}
                    page={validPage}
                    onChangePage={onPageChange}
                    rowsPerPage={pagination.pageSize}
                    onChangeRowsPerPage={onRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            )}
        </Card>
    )
}
